import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { hasLocation, markerIconUrl, nextStop, tripPoints } from "./trackingRules";

const START = { center: [19.0936, 72.856629], zoom: 10 };
const SELECTED_ZOOM = 17;

const carIcon = (v) =>
  L.divIcon({
    html: `<div class="trk-car"><img src="${markerIconUrl(v.bearing)}" alt="" /><div class="trk-plate">${String(v.vehicleNo ?? "").replace(/</g, "&lt;")}</div></div>`,
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
const pinIcon = (kind) =>
  L.divIcon({
    html: `<div class="trk-pin trk-pin--${kind}"><i class="fas fa-${kind === "pickup" ? "map-marker-alt" : "flag-checkered"}"></i></div>`,
    className: "",
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });

/**
 * Leaflet map of the vehicles on job (dark OSM tiles). Markers are kept per ride, so a refresh moves the cars instead of
 * redrawing them. The selected vehicle gets a 50 m circle, its pickup / dropoff pins and a dashed line to its next stop.
 * fitKey: changes after each Search / Clear to fit the map to the cars; centerKey: changes to center on the selected one.
 */
export default function TrackingMap({ rows, selected, onSelect, fitKey, centerKey }) {
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const carsRef = useRef({ layer: null, byRide: new Map() });
  const tripRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // The map, once.
  useEffect(() => {
    const map = L.map(boxRef.current, START);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    carsRef.current.layer = L.layerGroup().addTo(map);
    tripRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // The panel opening / closing beside the map changes its width.
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => map.invalidateSize()) : null;
    if (observer) observer.observe(boxRef.current);
    return () => {
      if (observer) observer.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Cars: add, move or remove per ride.
  useEffect(() => {
    const { layer, byRide } = carsRef.current;
    const seen = new Set();
    rows.filter(hasLocation).forEach((v) => {
      seen.add(v.rideId);
      const at = [Number(v.vehicleLatitude), Number(v.vehicleLongitude)];
      let marker = byRide.get(v.rideId);
      if (!marker) {
        marker = L.marker(at, { icon: carIcon(v) }).addTo(layer);
        marker.on("click", () => onSelectRef.current(marker.rideId));
        byRide.set(v.rideId, marker);
      } else {
        marker.setLatLng(at);
        if (marker.iconKey !== `${markerIconUrl(v.bearing)}|${v.vehicleNo}`) marker.setIcon(carIcon(v));
      }
      marker.rideId = v.rideId;
      marker.iconKey = `${markerIconUrl(v.bearing)}|${v.vehicleNo}`;
    });
    byRide.forEach((marker, rideId) => {
      if (!seen.has(rideId)) {
        marker.remove();
        byRide.delete(rideId);
      }
    });
  }, [rows]);

  // After a Search / Clear: fit to the cars (or back to the start view when there are none).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const points = rows.filter(hasLocation).map((v) => [Number(v.vehicleLatitude), Number(v.vehicleLongitude)]);
    if (points.length) map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 });
    else map.setView(START.center, START.zoom);
    // rows is read on purpose only when fitKey changes (not on every 30 s refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  // The selected trip: circle, pins and the dashed line to the next stop.
  const sel = selected && hasLocation(selected) ? selected : null;
  const selKey = sel ? [sel.rideId, sel.vehicleLatitude, sel.vehicleLongitude, sel.isBoarded, sel.isDeparted].join("|") : "";
  useEffect(() => {
    const layer = tripRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!sel) return;
    const at = [Number(sel.vehicleLatitude), Number(sel.vehicleLongitude)];
    L.circle(at, { radius: 50, color: "#095590", weight: 1.5, fillColor: "#095590", fillOpacity: 0.12 }).addTo(layer);
    const { pickup, dropoff } = tripPoints(sel);
    if (pickup) L.marker([pickup.lat, pickup.lng], { icon: pinIcon("pickup"), title: `Pick-up: ${sel.pickupLocation ?? ""}` }).addTo(layer);
    if (dropoff) L.marker([dropoff.lat, dropoff.lng], { icon: pinIcon("dropoff"), title: `Dropoff: ${sel.dropoffLocation ?? ""}` }).addTo(layer);
    const stop = nextStop(sel);
    if (stop) L.polyline([at, [stop.lat, stop.lng]], { className: "trk-route", weight: 3, dashArray: "8 8", interactive: false }).addTo(layer);
    // selKey holds every field the drawing uses
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  // Center on the selected car when it is picked, or on "Center on map".
  const selId = sel?.rideId ?? null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sel) return;
    map.setView([Number(sel.vehicleLatitude), Number(sel.vehicleLongitude)], Math.max(map.getZoom(), SELECTED_ZOOM), { animate: true });
    // only on a new pick or an explicit center request, not when the car moves on a refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, centerKey]);

  return <div ref={boxRef} className="tracking-map-dark tc-map" aria-label="Map of the vehicles on job" />;
}
